import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { merge, of } from 'rxjs';
import {
    catchError,
    exhaustMap,
    map,
    tap,
} from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import * as AuthActions from './auth.actions';
import { NotifierService } from 'src/app/app/services/notifier.service';
import { ResponseError } from './auth.state';
import { DynamicUiService } from 'src/app/dynamic-ui/services/dynamic-ui.service';
import { AppConstants } from 'src/app/app/helpers/app-constants';
import { getSavedState, setSavedState } from '../../shared/helpers/session-storage-helper';
import { values } from 'lodash-es';

function mergeArrays(a: any[], b: any[]): any[] {
    const map = new Map<string, any>();
    for (const it of a) map.set(it.name, it);
    for (const it of b) map.set(it.name, it); // überschreibt bei gleichem namen
    return Array.from(map.values());
}

@Injectable()
export class AuthEffects {
    constructor(
        private router: Router,
        private actions$: Actions,
        private authService: AuthService,
        private activatedRoute: ActivatedRoute,
        private notifierService: NotifierService,
        private dynamicService: DynamicUiService
    ) { }

    login$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.login),
            exhaustMap((credentials) =>
                this.authService
                    .login(
                        credentials.username,
                        credentials.password,
                        credentials.rememberThisUser
                    )
                    .pipe(
                        map((response) => {
                            return AuthActions.loginSuccess({ response });
                        }),
                        catchError((error) => {
                            return of(
                                AuthActions.loginFailure({
                                    error: {
                                        ...new ResponseError(
                                            error.message,
                                            error.statusCode
                                        ),
                                        error: error.error,
                                    },
                                })
                            );
                        })
                    )
            )
        );
    });

    onLoginSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.loginSuccess),
            map((response) => {
                this.authService.setSessionGuid(response.response.sessionGuid);
                // redirect to return url or home
                this.router.navigateByUrl(
                    this.activatedRoute.snapshot.queryParams.returnUrl || '/'
                );
                return AuthActions.getAuthUserRequest();
            })
        );
    });

    logout$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(AuthActions.logout),
                exhaustMap(() =>
                    this.authService.logout().pipe(
                        map(() => {
                            return this.router.navigateByUrl('/');
                        }),
                        catchError((error) =>
                            of(
                                AuthActions.refreshTokenFailure({
                                    error: error,
                                })
                            )
                        )
                    )
                )
            );
        },
        { dispatch: false }
    );

    getUser$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                AuthActions.refreshTokenSuccess,
                AuthActions.getAuthUserRequest
            ),
            exhaustMap(() =>
                this.authService.getAuthUser().pipe(
                    map((user) => {
                        // Set session values
                        if (this.authService.getSecretGuid() == user.secretGuid) {
                            user.sessionEntries.map(se => {
                                // direkt in session speichern
                                var savedState = getSavedState();
                                if (!savedState) { savedState = { session: {} }; }
                                savedState["session"][se.name] = se.value;
                                setSavedState(savedState);
                                this.dynamicService.SetValue(
                                    "session." + se.name + ".",
                                    [],
                                    se.value
                                );
                            });
                        }
                        else {
                            // Check if session for selectedClub ist set
                            var currentClub = this.dynamicService.GetValue(
                                AppConstants.dataStoreCurrentClub,
                                []
                            );
                            if (!(currentClub?.id > 0)) {
                                user.sessionEntries.map(se => {
                                    // direkt in session speichern
                                    var savedState = getSavedState();
                                    if (!savedState) { savedState = { session: {} }; }
                                    savedState["session"][se.name] = se.value;
                                    setSavedState({ session: savedState });
                                });
                            }
                        }
                        return AuthActions.getAuthUserSuccess({ user });
                    }),
                    catchError(() => of(AuthActions.getAuthUserFailure()))
                )
            )
        );
    });

    refreshToken$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.refreshToken),
            exhaustMap(() =>
                this.authService.refreshToken().pipe(
                    map(() => {
                        return AuthActions.refreshTokenSuccess();
                    }),
                    catchError((error) =>
                        of(AuthActions.refreshTokenFailure(error))
                    )
                )
            )
        );
    });

    onLoginOrRefreshTokenFailure$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    AuthActions.loginFailure,
                    AuthActions.refreshTokenFailure
                ),
                tap((_) => {
                    this.notifierService.showNotificationError(
                        _.error.error?.errorMessage ?? _.error.message
                    );
                    //this.authFacade.logout();
                })
            );
        },
        { dispatch: false }
    );

    onSignUp$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.signup),
            exhaustMap((credentials) =>
                this.authService.signUp(credentials.signUpRequest).pipe(
                    map((data) => {
                        return AuthActions.signUpSuccess();
                    }),
                    catchError((error) =>
                        of(AuthActions.signUpFailure({ error }))
                    )
                )
            )
        );
    });

    onSignUpSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(AuthActions.signUpSuccess),
                tap(() => {
                    this.notifierService.showNotificationSuccess(
                        'Ihr Konto wurde erfolgreich erstellt.'
                    );
                    this.router.navigateByUrl('/login');
                })
            );
        },
        { dispatch: false }
    );

    onSignUpFailure$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(AuthActions.signUpFailure),
                tap((error) => {
                    this.notifierService.showNotificationError(
                        error.error.error?.errorMessage ?? error.error.message
                    );
                })
            );
        },
        { dispatch: false }
    );

    onPasswordResetEmail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.passwordResetEmail),
            exhaustMap((credentials) =>
                this.authService.passwordResetEmail(credentials.passwordResetEmailRequest).pipe(
                    map((data) => {
                        return AuthActions.passwordResetEmailSuccess();
                    }),
                    catchError((error) =>
                        of(AuthActions.passwordResetEmailFailure({ error }))
                    )
                )
            )
        );
    });

    onPasswordResetEmailSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(AuthActions.passwordResetEmailSuccess),
                tap(() => {
                    this.notifierService.showNotificationSuccess(
                        'Das Mail wurde erfolgreich gesendet.'
                    );
                    this.router.navigateByUrl('/login');
                })
            );
        },
        { dispatch: false }
    );

    onPasswordResetEmailFailure$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(AuthActions.passwordResetEmailFailure),
                tap((error) => {
                    this.notifierService.showNotificationError(
                        error.error.error?.errorMessage ?? error.error.message
                    );
                })
            );
        },
        { dispatch: false }
    );

    onPasswordReset$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.passwordReset),
            exhaustMap((credentials) =>
                this.authService.passwordReset(credentials.passwordResetRequest).pipe(
                    map((data) => {
                        return AuthActions.passwordResetSuccess();
                    }),
                    catchError((error) =>
                        of(AuthActions.passwordResetFailure({ error }))
                    )
                )
            )
        );
    });

    onPasswordResetSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(AuthActions.passwordResetSuccess),
                tap(() => {
                    this.notifierService.showNotificationSuccess(
                        'Das neue Passwort wurde erfolgreich gesetzt.'
                    );
                    this.router.navigateByUrl('/login');
                })
            );
        },
        { dispatch: false }
    );

    onPasswordResetFailure$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(AuthActions.passwordResetFailure),
                tap((error) => {
                    this.notifierService.showNotificationError(
                        error.error.error?.errorMessage ?? error.error.message
                    );
                })
            );
        },
        { dispatch: false }
    );
}
